import { grey10, navy } from '@zocdoc/branding/colors';
import SwipeHandler from '@zocdoc/components/src/SwipeHandler';
import { Arrow } from '@zocdoc/components/src/icons';
import { FontFamily } from '@zocdoc/components/src/typography';
import PropTypes from 'prop-types';
import React from 'react';
import { tween } from 'shifty';
import styled from 'styled-components';
import DoctorCard from './CompactDoctorCard';

export class LocationCardCarousel extends React.PureComponent {
    static propTypes = {
        providers: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                location: PropTypes.shape({
                    address1: PropTypes.string.isRequired,
                    city: PropTypes.string.isRequired,
                    latitude: PropTypes.number.isRequired,
                    longitude: PropTypes.number.isRequired,
                    state: PropTypes.string.isRequired,
                    zipCode: PropTypes.string.isRequired,
                }).isRequired,
                provider: PropTypes.shape({
                    firstName: PropTypes.string.isRequired,
                    lastName: PropTypes.string.isRequired,
                }).isRequired,
            })
        ),

        showPage: PropTypes.number,
    }

    static getDerivedStateFromProps(nextProps, state) {
        if (nextProps.providers !== state.providers) {
            return {
                currentPage: 1,
                providers: nextProps.providers,
                totalPages: nextProps.providers.length,
            };
        }

        return null;
    }

    state = {}
    swiperRef = React.createRef()

    componentDidMount() {
        if (this.props.showPage) {
            this.swiperRef.current.scrollLeft = COMPACT_DC_WIDTH * (this.props.showPage - 1);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.currentPage > prevState.currentPage) {
            tween({
                from: { x: this.swiperRef.current.scrollLeft },
                to: { x: (this.state.currentPage - 1) * COMPACT_DC_WIDTH },
                duration: 200,
                step: ({ x }) => (this.swiperRef.current.scrollLeft = x),
            });
        } else if (this.state.currentPage < prevState.currentPage) {
            tween({
                from: { x: this.swiperRef.current.scrollLeft },
                to: { x: (this.state.currentPage - 1) * COMPACT_DC_WIDTH },
                duration: 200,
                step: ({ x }) => (this.swiperRef.current.scrollLeft = x),
            });
        } else if (this.props.showPage !== prevProps.showPage) {
            this.swiperRef.current.scrollLeft = COMPACT_DC_WIDTH * (this.props.showPage - 1);
        }
    }

    decrementPage = () => this.setState(prevState => ({
        currentPage: Math.max(1, prevState.currentPage - 1),
    }))

    incrementPage = () => this.setState(prevState => ({
        currentPage: Math.min(this.props.providers.length, prevState.currentPage + 1),
    }))

    handleKeyDown = e => {
        if (e.key === 'Enter') {
            if (e.currentTarget.getAttribute('data-test') === 'previous') {
                this.decrementPage();
            } else {
                this.incrementPage();
            }
        }
    }

    render() {
        return (
            <Container data-test="provider-map-carousel">
                {this.maybeRenderHeader()}

                <SwipeHandler
                    direction={6}
                    onSwipeLeft={this.incrementPage}
                    onSwipeRight={this.decrementPage}
                >
                    <SwipeViewport
                        data-test="provider-map-carousel-viewport"
                        innerRef={this.swiperRef}
                    >
                        <SwipeRail count={this.props.providers.length}>
                            {this.props.providers.map(({ id, location, provider }) => (
                                <StyledDoctorCard
                                    key={id}
                                    data-test="provider-map-carousel-card"
                                    location={location}
                                    locationId={location.id}
                                    provider={provider}
                                    providerId={provider.id}
                                />
                            ))}
                        </SwipeRail>
                    </SwipeViewport>
                </SwipeHandler>
            </Container>
        );
    }

    maybeRenderHeader() {
        if (this.props.providers.length > 1) {
            return (
                <SwipeHeader data-test="provider-map-carousel-header">
                    <StyledArrow
                        aria-label="Go to previous doctor card"
                        color={this.state.currentPage !== 1 ? navy : 'transparent'}
                        data-test="provider-map-carousel-header-previous"
                        direction="left"
                        height={10}
                        onClick={this.decrementPage}
                        onKeyDown={this.handleKeyDown}
                        role="button"
                    />

                    {this.state.currentPage} / {this.props.providers.length}

                    <StyledArrow
                        aria-label="Go to next doctor card"
                        color={this.state.currentPage !== this.state.totalPages ? navy : 'transparent'}
                        data-test="provider-map-carousel-header-next"
                        direction="right"
                        height={10}
                        onClick={this.incrementPage}
                        onKeyDown={this.handleKeyDown}
                        role="button"
                    />
                </SwipeHeader>
            );
        }
    }
}

export const COMPACT_DC_WIDTH = 234;

const SwipeHeader = styled.header`
    align-items: center;
    border-bottom: 2px solid ${grey10};
    display: flex;
    font-family: ${FontFamily.SharpSansBold};
    font-size: 11px;
    justify-content: space-between;
    margin-bottom: 11px;
    padding: 0 0 6px;
    user-select: none;
`;

// the padding + margin help create a larger hitbox, since 12px is rather small
const StyledArrow = styled(Arrow)`
    cursor: pointer;
    margin: -9px -10px -10px;
    padding: 9px 10px 10px;
`;

const SwipeViewport = styled.div`
    overflow: hidden;
    position: relative;
    width: ${COMPACT_DC_WIDTH}px;
`;

const SwipeRail = styled.div`
    display: flex;
    width: ${p => p.count * COMPACT_DC_WIDTH}px;
`;

const StyledDoctorCard = styled(DoctorCard)`
    display: inline-flex;
    margin-bottom: 2px;
    width: ${COMPACT_DC_WIDTH}px;
    vertical-align: top;
`;

const Container = styled.div`
    box-sizing: content-box;
`;
